// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import usePageSize from "@/hooks/usePageSize";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /** The page being shown, 1-based. */
    page: number;
    /** How many rows exist behind the filter, not how many arrived. */
    total: number;
    /** Rows actually on screen. The last page is short, and the label should say so. */
    count: number;
    onPage: (page: number) => void;
};

/** Where the window around the current page gives way to an ellipsis. */
const EDGE = 1;
const AROUND = 1;

/**
 * The numbers to draw: the first page, the last, the ones either side of where you are, and an
 * ellipsis for each run that was left out. Nine pages fit; nine hundred have to be summarised.
 */
function buildPages(page: number, pages: number): (number | "gap")[] {
    const wanted = new Set<number>();
    for (let i = 1; i <= Math.min(EDGE, pages); i++) wanted.add(i);
    for (let i = Math.max(1, pages - EDGE + 1); i <= pages; i++) wanted.add(i);
    for (let i = Math.max(1, page - AROUND); i <= Math.min(pages, page + AROUND); i++) wanted.add(i);

    const sorted = [...wanted].sort((a, b) => a - b);
    const out: (number | "gap")[] = [];
    for (const [index, value] of sorted.entries()) {
        // A gap of exactly one page is drawn as that page: "… 7 …" is longer than "6 7 8".
        if (index > 0 && value - sorted[index - 1] > 1) out.push("gap");
        out.push(value);
    }
    return out;
}

/**
 * The pager every list ends with: the arrows, the page numbers, how many rows are showing out of how
 * many there are, and how many to show at a time — all in one bar, because they are one decision
 * taken in one place rather than four controls scattered along an edge.
 *
 * It floats at the bottom of its scrolling area, so moving through a long list never means scrolling
 * to the bottom to find the control that scrolls you. An empty list is the one case it skips: the
 * screen already says it is empty, and "0 of 0" says it again in figures.
 *
 * **The page size is one setting for the whole app.** Changing it here changes every other list too
 * and is remembered — see `usePageSize`. The screens pass a page and read `usePageSize` for the
 * limit; this component owns the control that sets it.
 */
export default function Paginator({ page, total, count, onPage }: Props) {
    const t = useTranslation();
    const { pageSize, setPageSize } = usePageSize();
    const pages = Math.max(1, Math.ceil(total / pageSize));

    /**
     * Uncontrolled, and keyed on the stored size so it reverts to it whenever that changes. A
     * controlled field would have to hold what is being typed — briefly not a number at all, "" on
     * the way from 24 to 40 — and mirroring the store back into that state is the cascading render
     * this avoids. The store hears about it on blur or Enter, never once per digit.
     */
    const commitSize = (value: string) => {
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed) || parsed === pageSize) return;
        setPageSize(parsed);
        // Whatever page you were on may not exist at the new size, so the list starts again.
        onPage(1);
    };

    if (!total) return null;

    const go = (next: number) => next >= 1 && next <= pages && next !== page && onPage(next);

    return (
        <nav className="paginator" aria-label={t.common.pagination}>
            <div className="paginator__bar">
                <span className="paginator__count">{t.common.pageCount.replace("{count}", String(count)).replace("{total}", String(total))}</span>

                <span className="paginator__divider" />

                <button
                    type="button"
                    className="paginator__arrow paginator__arrow--prev"
                    onClick={() => go(page - 1)}
                    disabled={page === 1}
                    aria-label={t.common.previousPage}
                >
                    <Icon name="chevron" size={14} />
                </button>

                {buildPages(page, pages).map((entry, index) =>
                    entry === "gap" ? (
                        <span key={`gap-${index}`} className="paginator__gap" aria-hidden="true">
                            …
                        </span>
                    ) : (
                        <button
                            key={entry}
                            type="button"
                            className={`paginator__page${entry === page ? " paginator__page--current" : ""}`}
                            onClick={() => go(entry)}
                            aria-current={entry === page ? "page" : undefined}
                        >
                            {entry}
                        </button>
                    )
                )}

                <button
                    type="button"
                    className="paginator__arrow paginator__arrow--next"
                    onClick={() => go(page + 1)}
                    disabled={page === pages}
                    aria-label={t.common.nextPage}
                >
                    <Icon name="chevron" size={14} />
                </button>

                <span className="paginator__divider" />

                <label className="paginator__size">
                    <input
                        key={pageSize}
                        type="number"
                        className="paginator__size-input"
                        defaultValue={pageSize}
                        min={constants.minPageSize}
                        max={constants.maxPageSize}
                        onBlur={(event) => commitSize(event.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
                        aria-label={t.common.perPage}
                    />
                    <span className="paginator__size-label">{t.common.perPage}</span>
                </label>
            </div>
        </nav>
    );
}
